import * as React from 'react'
import { AlertCircle, ImagePlus, Loader2, Plus, X } from 'lucide-react'

import { isGalleryVideoEnabled } from '@/shared/config'
import { cn } from '@/shared/ui/cn'
import { Button } from '@/shared/ui/primitives'

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024
const MAX_VIDEO_SIZE_BYTES = 150 * 1024 * 1024
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime']

type PendingPhotoStatus = 'pending' | 'uploading' | 'uploaded' | 'error'

type PendingPhoto = {
  id: string
  file: File
  previewUrl: string
  status?: PendingPhotoStatus
  errorMessage?: string
}

type PhotoUploadProps = {
  pendingPhotos: PendingPhoto[]
  onAdd: (files: File[]) => void
  onRemove: (id: string) => void
  maxPhotos: number
  existingCount?: number
}

function PhotoUpload({
  pendingPhotos,
  onAdd,
  onRemove,
  maxPhotos,
  existingCount = 0,
}: PhotoUploadProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const videoEnabled = isGalleryVideoEnabled()
  const acceptedTypes = videoEnabled
    ? [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES]
    : ACCEPTED_IMAGE_TYPES

  const totalCount = pendingPhotos.length + existingCount
  const isAtCapacity = totalCount >= maxPhotos

  const validateAndAdd = (files: FileList | null) => {
    if (!files || files.length === 0) return

    setError(null)

    const remaining = maxPhotos - totalCount
    const validFiles: File[] = []

    for (const file of Array.from(files)) {
      const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type)
      const isVideo = videoEnabled && ACCEPTED_VIDEO_TYPES.includes(file.type)
      if (!isImage && !isVideo) {
        setError('Only PNG, JPEG, and WebP images are accepted')
        return
      }
      if (isVideo && file.size > MAX_VIDEO_SIZE_BYTES) {
        setError('Each video must be under 150 MB')
        return
      }
      if (isImage && file.size > MAX_FILE_SIZE_BYTES) {
        setError('Each file must be under 10 MB')
        return
      }
      if (validFiles.length < remaining) {
        validFiles.push(file)
      }
    }

    if (validFiles.length > 0) {
      onAdd(validFiles)
    }
  }

  const handleClick = () => {
    if (isAtCapacity) return
    inputRef.current?.click()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateAndAdd(e.target.files)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!isAtCapacity) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    if (!isAtCapacity) {
      validateAndAdd(e.dataTransfer.files)
    }
  }

  return (
    <div data-slot="photo-upload" className="flex flex-col gap-3">
      {pendingPhotos.length === 0 ? (
        <div
          role="button"
          tabIndex={isAtCapacity ? -1 : 0}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleClick()
            }
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors',
            isDragOver && 'border-primary bg-primary/5',
            isAtCapacity && 'cursor-not-allowed opacity-50',
            !isDragOver && !isAtCapacity && 'border-muted-foreground/25 hover:border-primary/50',
          )}
        >
          <ImagePlus className="size-8 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {isAtCapacity
              ? `Up to ${maxPhotos} photos`
              : 'Add photos'}
          </span>
        </div>
      ) : (
        <div
          role="group"
          aria-label="Pending photo uploads"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'grid grid-cols-4 gap-2 rounded-md p-1 transition-colors sm:grid-cols-6 md:grid-cols-8',
            isDragOver && 'ring-2 ring-primary/50',
          )}
        >
          {pendingPhotos.map((photo) => (
            <div
              key={photo.id}
              className={cn(
                'group relative aspect-square overflow-hidden rounded-md',
                photo.status === 'error' && 'ring-2 ring-destructive',
              )}
              title={photo.status === 'error' ? photo.errorMessage : undefined}
            >
              <img
                src={photo.previewUrl}
                alt=""
                className="size-full object-cover"
              />
              {photo.status === 'uploading' ? (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <Loader2 className="size-5 animate-spin text-white" />
                </div>
              ) : null}
              {photo.status === 'error' ? (
                <div className="absolute left-1 top-1 rounded-full bg-destructive p-0.5 text-destructive-foreground">
                  <AlertCircle className="size-3.5" />
                </div>
              ) : null}
              <Button
                type="button"
                variant="destructive"
                size="icon-xs"
                className="absolute right-1 top-1 opacity-0 transition-opacity group-hover:opacity-100"
                onClick={() => onRemove(photo.id)}
                aria-label="Remove photo"
              >
                <X />
              </Button>
            </div>
          ))}
          {!isAtCapacity ? (
            <button
              type="button"
              onClick={handleClick}
              aria-label="Add more photos"
              className="flex aspect-square items-center justify-center rounded-md border-2 border-dashed border-muted-foreground/25 transition-colors hover:border-primary/50"
            >
              <Plus className="size-6 text-muted-foreground" />
            </button>
          ) : null}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        multiple
        className="hidden"
        onChange={handleInputChange}
      />

      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : null}
    </div>
  )
}

export { PhotoUpload }
export type { PhotoUploadProps, PendingPhoto, PendingPhotoStatus }
